// src/components/organization/OrganizationSettings.jsx
import React, { useState, useEffect } from 'react';
import { useOrganization } from '../../hooks/useOrganization';
import LoadingSpinner from '../common/LoadingSpinner';
import { PencilSquareIcon, CheckIcon, XMarkIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { SUPPORTED_CURRENCIES, SUPPORTED_LANGUAGES } from '../../utils/constants';
import { useTranslation } from 'react-i18next';

const Field = ({ label, value, children, editing }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
            {label}
        </label>
        {editing
            ? children
            : <p className="text-sm text-gray-900 font-medium">{value || <span className="text-gray-400 font-normal">—</span>}</p>
        }
    </div>
);

const ic = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition';

const OrganizationSettings = () => {
    const { t } = useTranslation();
    const { organization, updateOrganization, loading } = useOrganization();
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [form, setForm] = useState({
        name: '', email: '', phone: '', address: '',
        city: '', country: '', currency: 'EUR', language: 'it',
    });

    useEffect(() => {
        if (organization) {
            setForm({
                name: organization.name || '',
                email: organization.email || '',
                phone: organization.phone || '',
                address: organization.address || '',
                city: organization.city || '',
                country: organization.country || '',
                currency: organization.currency || 'EUR',
                language: organization.language || 'it',
            });
        }
    }, [organization]);

    const handleChange = (e) =>
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleCancel = () => {
        if (organization) {
            setForm({
                name: organization.name || '',
                email: organization.email || '',
                phone: organization.phone || '',
                address: organization.address || '',
                city: organization.city || '',
                country: organization.country || '',
                currency: organization.currency || 'EUR',
                language: organization.language || 'it',
            });
        }
        setEditing(false);
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { setError('Il nome è obbligatorio'); return; }
        setSaving(true);
        setError(null);
        // updateOrganization chiama già refreshOrganization internamente
        const result = await updateOrganization(form);
        setSaving(false);
        if (result?.success !== false) {
            setEditing(false);
        } else {
            setError(result.error || 'Errore durante il salvataggio');
        }
    };

    if (loading) return <LoadingSpinner />;

    const currencyLabel = SUPPORTED_CURRENCIES.find(c => c.code === organization?.currency)?.label;
    const languageLabel = SUPPORTED_LANGUAGES.find(l => l.code === organization?.language)?.label;

    return (
        <div className="space-y-5">
            <div className="bg-white rounded-2xl border overflow-hidden">

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                            <BuildingOfficeIcon className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">{organization?.name || 'Organizzazione'}</h2>
                            <p className="text-blue-200 text-sm mt-0.5">{organization?.email || '—'}</p>
                        </div>
                    </div>
                </div>

                <div className="p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                            ⚠️ {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                            <Field label="Nome organizzazione" value={organization?.name} editing={editing}>
                                <input type="text" name="name" value={form.name} onChange={handleChange}
                                    className={ic} required />
                            </Field>

                            <Field label="Email" value={organization?.email} editing={editing}>
                                <input type="email" name="email" value={form.email} onChange={handleChange}
                                    className={ic} />
                            </Field>

                            <Field label="Telefono" value={organization?.phone} editing={editing}>
                                <input type="tel" name="phone" value={form.phone} onChange={handleChange}
                                    className={ic} placeholder="+39 000 000 0000" />
                            </Field>

                            <Field label="Paese (codice ISO)" value={organization?.country} editing={editing}>
                                <input type="text" name="country" value={form.country} onChange={handleChange}
                                    className={ic} placeholder="IT" maxLength={2}
                                    style={{ textTransform: 'uppercase' }} />
                            </Field>

                            <Field label="Città" value={organization?.city} editing={editing}>
                                <input type="text" name="city" value={form.city} onChange={handleChange}
                                    className={ic} />
                            </Field>

                            <Field label="Indirizzo" value={organization?.address} editing={editing}>
                                <input type="text" name="address" value={form.address} onChange={handleChange}
                                    className={ic} />
                            </Field>

                            <Field label="Valuta" value={currencyLabel} editing={editing}>
                                <select name="currency" value={form.currency} onChange={handleChange} className={ic}>
                                    {SUPPORTED_CURRENCIES.map(c => (
                                        <option key={c.code} value={c.code}>
                                            {c.symbol} {c.code} — {c.label}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field label="Lingua" value={languageLabel} editing={editing}>
                                <select name="language" value={form.language} onChange={handleChange} className={ic}>
                                    {SUPPORTED_LANGUAGES.map(l => (
                                        <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
                                    ))}
                                </select>
                            </Field>

                        </div>

                        <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-gray-100">
                            {editing ? (
                                <>
                                    <button type="button" onClick={handleCancel}
                                        className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition">
                                        <XMarkIcon className="w-4 h-4" /> Annulla
                                    </button>
                                    <button type="submit" disabled={saving}
                                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50">
                                        {saving
                                            ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Salvataggio...</>
                                            : <><CheckIcon className="w-4 h-4" /> Salva modifiche</>
                                        }
                                    </button>
                                </>
                            ) : (
                                <button type="button" onClick={() => setEditing(true)}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition">
                                    <PencilSquareIcon className="w-4 h-4" /> Modifica
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default OrganizationSettings;